import React, { useState, useEffect } from "react";
import {
  Save,
  Mail,
  Server,
  Key,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../contexts/AuthContext";

export default function EmailConfigPage() {
  const { authAxios } = useAuth();
  const [config, setConfig] = useState({
    enabled: false,
    host: "",
    port: 587,
    username: "",
    password: "",
    sender_email: "",
    recipient_email: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await authAxios.get("/email-config");
      setConfig(res.data);
    } catch (err) {
      console.error("Failed to fetch email config", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await authAxios.put("/email-config", config);
      setMessage({ type: "success", text: "Email configuration saved successfully!" });
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to save configuration" });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const res = await authAxios.post("/email-config/test");
      setMessage({ type: "success", text: res.data.message });
      setTimeout(() => setMessage(null), 6000);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Test failed" });
    } finally {
      setTesting(false);
    }
  };

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading email configuration...
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Email Notifications</h1>
          <p className="text-gray-500 text-sm">
            Configure SMTP settings to receive email notifications for new quote requests
          </p>
        </div>
        {message && (
          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
              message.type === "success"
                ? "bg-emerald-500/10 text-emerald-400"
                : "bg-red-500/10 text-red-400"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            {message.text}
          </div>
        )}
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="bg-[#111118] border border-white/[0.04] rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center">
                <Mail className="h-5 w-5 text-[#DC2626]" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Email Notifications</h3>
                <p className="text-gray-500 text-xs">
                  Receive an email when someone submits a quote request
                </p>
              </div>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => updateField("enabled", checked)}
            />
          </div>
        </div>

        {/* SMTP Server Settings */}
        <div className="bg-[#111118] border border-white/[0.04] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Server className="h-4 w-4 text-[#DC2626]" />
            <h3 className="text-white font-semibold text-sm">SMTP Server Settings</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">SMTP Host</Label>
              <Input
                value={config.host}
                onChange={(e) => updateField("host", e.target.value)}
                placeholder="smtp.example.com"
                className="bg-[#0A0A0F] border-white/[0.06] text-white text-sm h-10"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">SMTP Port</Label>
              <Input
                type="number"
                value={config.port}
                onChange={(e) => updateField("port", parseInt(e.target.value) || 587)}
                placeholder="587"
                className="bg-[#0A0A0F] border-white/[0.06] text-white text-sm h-10"
              />
              <p className="text-gray-600 text-xs">587 for TLS, 465 for SSL</p>
            </div>
          </div>
        </div>

        {/* Authentication */}
        <div className="bg-[#111118] border border-white/[0.04] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Key className="h-4 w-4 text-[#DC2626]" />
            <h3 className="text-white font-semibold text-sm">Authentication</h3>
          </div>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">Username / Email</Label>
              <Input
                value={config.username}
                onChange={(e) => updateField("username", e.target.value)}
                placeholder="your-email@example.com"
                className="bg-[#0A0A0F] border-white/[0.06] text-white text-sm h-10"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">Password / App Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={config.password}
                  onChange={(e) => updateField("password", e.target.value)}
                  placeholder="••••••••"
                  className="bg-[#0A0A0F] border-white/[0.06] text-white text-sm h-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Email Addresses */}
        <div className="bg-[#111118] border border-white/[0.04] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Mail className="h-4 w-4 text-[#DC2626]" />
            <h3 className="text-white font-semibold text-sm">Email Addresses</h3>
          </div>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">Sender Email (From)</Label>
              <Input
                type="email"
                value={config.sender_email}
                onChange={(e) => updateField("sender_email", e.target.value)}
                placeholder="noreply@yourdomain.com"
                className="bg-[#0A0A0F] border-white/[0.06] text-white text-sm h-10"
              />
              <p className="text-gray-600 text-xs">Email address that will appear as the sender</p>
            </div>
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">Recipient Email (To)</Label>
              <Input
                type="email"
                value={config.recipient_email}
                onChange={(e) => updateField("recipient_email", e.target.value)}
                placeholder="you@yourdomain.com"
                className="bg-[#0A0A0F] border-white/[0.06] text-white text-sm h-10"
              />
              <p className="text-gray-600 text-xs">
                Email address where quote notifications will be sent
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-white/[0.04]">
          <Button
            onClick={handleTest}
            disabled={testing || !config.host || !config.username}
            variant="outline"
            className="border-white/10 text-gray-300 hover:text-white hover:bg-white/[0.04]"
          >
            {testing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {testing ? "Sending..." : "Send Test Email"}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-8 font-medium"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
        </div>
      </div>
    </div>
  );
}
